/**
 * Display Modes
 * Theater mode and fullscreen mode functionality
 */

import { uiState } from './state.js';
import { elements } from './dom-cache.js';
import { closeAllPanels, toggleSettings, isSettingsOpen } from './overlay-panels.js';

// Constants for immersive mode controls
const CONTROLS_HIDE_DELAY = 3000; // 3 seconds

/**
 * Toggle theater mode (fullscreen-like view without actual fullscreen)
 */
export function toggleTheaterMode() {
  uiState.isTheaterMode = !uiState.isTheaterMode;
  document.body.classList.toggle('theater-mode', uiState.isTheaterMode);

  // Close all overlays when entering theater mode
  if (uiState.isTheaterMode) {
    closeAllPanels();
    if (isSettingsOpen()) {
      toggleSettings();
    }
  } else {
    // Hide immersive controls when exiting theater mode
    hideImmersiveControls();
  }

  // Update button appearance (legacy button)
  if (elements.theaterModeBtn) {
    elements.theaterModeBtn.innerHTML = uiState.isTheaterMode ? '&#x2716;' : '&#x26F6;';
    elements.theaterModeBtn.title = uiState.isTheaterMode ? 'Exit Theater Mode' : 'Theater Mode';
  }
}

/**
 * Toggle browser fullscreen mode
 */
export function toggleFullscreen() {
  // Use canvas-viewport for fullscreen so controls are included
  const container = document.querySelector('.canvas-viewport');

  if (!container) {
    console.warn('No canvas viewport found for fullscreen');
    return;
  }

  if (!document.fullscreenElement) {
    // Move transport bar inside fullscreen container before entering
    moveTransportBarForFullscreen(true);

    container.requestFullscreen().catch(err => {
      console.error('Fullscreen error:', err);
      // Restore transport bar position on error
      moveTransportBarForFullscreen(false);
      alert('Could not enter fullscreen: ' + err.message);
    });
  } else {
    document.exitFullscreen();
  }
}

// Store original transport bar parent for restoration
let originalTransportBarParent = null;
let originalTransportBarNextSibling = null;

/**
 * Move transport bar into/out of fullscreen container
 */
function moveTransportBarForFullscreen(entering) {
  const transportBar = elements.transportBar;
  const canvasViewport = document.querySelector('.canvas-viewport');

  if (!transportBar || !canvasViewport) return;

  if (entering) {
    // Store original position
    originalTransportBarParent = transportBar.parentElement;
    originalTransportBarNextSibling = transportBar.nextSibling;

    // Move into canvas viewport
    canvasViewport.appendChild(transportBar);
  } else {
    // Restore original position
    if (originalTransportBarParent) {
      if (originalTransportBarNextSibling) {
        originalTransportBarParent.insertBefore(transportBar, originalTransportBarNextSibling);
      } else {
        originalTransportBarParent.appendChild(transportBar);
      }
      originalTransportBarParent = null;
      originalTransportBarNextSibling = null;
    }
  }
}

/**
 * Set up display mode event listeners
 */
export function setupDisplayModeListeners() {
  // Theater mode button (legacy)
  if (elements.theaterModeBtn) {
    elements.theaterModeBtn.addEventListener('click', toggleTheaterMode);
  }

  // ESC key to exit theater mode
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && uiState.isTheaterMode && !document.fullscreenElement) {
      toggleTheaterMode();
    }
  });

  // Fullscreen button (legacy)
  if (elements.fullscreenBtn) {
    elements.fullscreenBtn.addEventListener('click', toggleFullscreen);
  }

  // Update button on fullscreen change
  document.addEventListener('fullscreenchange', () => {
    const isFullscreen = !!document.fullscreenElement;
    if (elements.fullscreenBtn) {
      elements.fullscreenBtn.innerHTML = isFullscreen ? '&#x2716;' : '&#x2922;';
      elements.fullscreenBtn.title = isFullscreen ? 'Exit Full Screen' : 'Full Screen';
    }

    // Restore transport bar position when exiting fullscreen
    if (!isFullscreen) {
      moveTransportBarForFullscreen(false);
    }
  });

  // Double-click on canvas to toggle theater mode.
  // Uses event delegation on the stable .canvas-viewport parent so the handler
  // survives canvas replacement when switching visualizer types (2D↔WebGPU).
  const canvasViewport = document.querySelector('.canvas-viewport');
  if (canvasViewport) {
    canvasViewport.addEventListener('dblclick', (e) => {
      if (e.target.tagName === 'CANVAS') toggleTheaterMode();
    });
  }

  // Set up immersive mode controls (click/mouse movement)
  setupImmersiveControlsListeners();
}

/**
 * Check if we're in an immersive mode (theater or fullscreen)
 */
export function isImmersiveMode() {
  return uiState.isTheaterMode || !!document.fullscreenElement;
}

/**
 * Show controls in immersive mode
 */
export function showImmersiveControls() {
  if (!isImmersiveMode()) return;

  uiState.immersiveControlsVisible = true;
  document.body.classList.add('immersive-controls-visible');

  // Reset the hide timer
  resetImmersiveControlsTimer();
}

/**
 * Hide controls in immersive mode
 */
export function hideImmersiveControls() {
  uiState.immersiveControlsVisible = false;
  document.body.classList.remove('immersive-controls-visible');

  // Clear any pending timeout
  if (uiState.immersiveControlsTimeout) {
    clearTimeout(uiState.immersiveControlsTimeout);
    uiState.immersiveControlsTimeout = null;
  }
}

/**
 * Reset the timer that hides controls after inactivity
 */
function resetImmersiveControlsTimer() {
  // Clear existing timeout
  if (uiState.immersiveControlsTimeout) {
    clearTimeout(uiState.immersiveControlsTimeout);
  }

  // Set new timeout to hide controls after delay
  uiState.immersiveControlsTimeout = setTimeout(() => {
    hideImmersiveControls();
  }, CONTROLS_HIDE_DELAY);
}

/**
 * Handle click in immersive mode to toggle controls
 */
function handleImmersiveClick(e) {
  if (!isImmersiveMode()) return;

  // Don't toggle if clicking on controls themselves
  const transportBar = elements.transportBar;
  const viewportControls = document.querySelector('.viewport-controls');
  const playlistOverlay = elements.playlistOverlay;

  if (transportBar?.contains(e.target) ||
      viewportControls?.contains(e.target) ||
      playlistOverlay?.contains(e.target)) {
    // Clicked on controls, just reset the timer
    resetImmersiveControlsTimer();
    return;
  }

  // Toggle controls visibility
  if (uiState.immersiveControlsVisible) {
    hideImmersiveControls();
  } else {
    showImmersiveControls();
  }
}

/**
 * Handle mouse movement in immersive mode to keep controls visible
 */
function handleImmersiveMouseMove() {
  if (!isImmersiveMode()) return;

  // Only reset timer if controls are already visible
  if (uiState.immersiveControlsVisible) {
    resetImmersiveControlsTimer();
  }
}

/**
 * Set up event listeners for immersive mode controls
 */
function setupImmersiveControlsListeners() {
  const canvasViewport = document.querySelector('.canvas-viewport');

  if (canvasViewport) {
    // Click to show/hide controls
    canvasViewport.addEventListener('click', handleImmersiveClick);

    // Mouse movement keeps controls visible
    canvasViewport.addEventListener('mousemove', handleImmersiveMouseMove);
  }

  // Hide controls when exiting immersive modes
  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement) {
      hideImmersiveControls();
    }
  });
}
